import { Helmet, HelmetData } from "react-helmet-async";
import { useRef } from "react";
import {
  AI_BG_NEW,
  AI_Inference_Hero_Img,
  Inference_Choose_Us_Img,
  What_you_get_Img,
} from "../../images";
import useIntersectionObserver from "../../utils/useIntersectionObserver";
import { CheckMark } from "../../icons";
import { NewFooter } from "../newFooter";
import BookConsultation from "../book-a-consultation";

const helmetData = new HelmetData({});

export default function AiInference() {
  const targetRef = useRef(null);

  useIntersectionObserver({ targetRef });
  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Secure AI Inference Infrastructure Africa | Private AI Hosting | Analytics Intelligence Africa</title>
        <link rel="canonical" href="https://africa.analyticsintelligence.com/ai-inference" />
        <meta
          name="description"
          content="Run private AI systems with enterprise-grade isolation, compliance controls, and predictable performance. Analytics Intelligence Africa provides secure AI inference infrastructure — on-prem, private cloud, or hybrid — for enterprises across Africa."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence Africa, AI inference Africa, secure AI infrastructure Africa, private AI hosting Africa, enterprise AI infrastructure Africa, AI on-premises Africa, private cloud AI Africa, AI compliance Africa, AI for banking Africa, AI for fintech Africa, model hosting Africa, AI deployment Africa, GPU AI Africa, RAG infrastructure Africa"
        />
        <meta property="og:title" content="Secure AI Inference Infrastructure | Analytics Intelligence Africa" />
        <meta property="og:description" content="Enterprise-grade private AI inference with workload isolation, compliance controls, and secure model hosting — Analytics Intelligence Africa." />
        <meta property="og:url" content="https://africa.analyticsintelligence.com/ai-inference" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Secure AI Inference Infrastructure | Analytics Intelligence Africa" />
        <meta name="twitter:description" content="Enterprise-grade private AI inference with workload isolation, compliance controls, and secure model hosting — Analytics Intelligence Africa." />
        <meta name="twitter:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "AI Inference Infrastructure",
            "url": "https://africa.analyticsintelligence.com/ai-inference",
            "description": "Secure AI inference infrastructure with enterprise-grade isolation, compliance controls, and predictable performance — on-prem, private cloud, or hybrid deployments for African enterprises.",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Africa",
              "url": "https://africa.analyticsintelligence.com"
            },
            "areaServed": ["Nigeria", "Ghana", "Kenya", "South Africa", "Rwanda", "Africa"]
          }
        `}</script>
      </Helmet>
      <main>
        {/* Hero Section */}
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center bg-left-bottom md:bg-center"
          ref={targetRef}
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16 md:gap-4 xl:gap-16  pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-4 flex flex-col items-center md:items-start">
                <span className="text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  AI Inference
                </span>
                <h2 className="text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white">
                  Secure AI Infrastructure
                </h2>
                <p className="text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                  Run private AI systems with enterprise-grade isolation,
                  compliance controls, and predictable performance—on-prem,
                  private cloud, or hybrid.
                </p>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className=" w-full"
                src={AI_Inference_Hero_Img}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section className="relative w-full bg-white py-[80px] md:py-[100px] bg-top bg-contain bg-no-repeat">
          <div className="mx-auto container">
            <div className="h-[300px] w-[300px] xl:h-[450px] xl:w-[450px] bg-[#7AA8FF80] blur-[130px] absolute top-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>

            {/* What We Do Section */}
            <div className="grid items-center gap-12 md:grid-cols-2 relative z-10">
              {/* Left side - text content */}
              <div className="space-y-3 md:space-y-6">
                <div className="space-y-1 md:space-y-3">
                  <h3 className=" text-2xl xl:text-[32px] font-bold">
                    What you get
                  </h3>
                </div>

                <ul className="space-y-2">
                  <li className="text-sm md:text-base flex items-start gap-2 text-gray-700">
                    <div>
                      <CheckMark />
                    </div>
                    <p className="w-fit">
                      <span className="font-bold">
                        Workload isolation by design
                      </span>
                      : tenant separation (VM/Kubernetes), GPU partitioning
                      where appropriate, network segmentation, and strict IAM.
                    </p>
                  </li>
                  <li className="text-sm md:text-base flex items-start gap-2 text-gray-700">
                    <div>
                      <CheckMark />
                    </div>
                    <p className="w-fit">
                      <span className="font-bold">
                        Data security end-to-end
                      </span>
                      : encryption in transit/at rest, key management, secrets
                      handling, audit logs, and least-privilege access.
                    </p>
                  </li>
                  <li className="text-sm md:text-base flex items-start gap-2 text-gray-700">
                    <div>
                      <CheckMark />
                    </div>
                    <p className="w-fit">
                      <span className="font-bold">Private model hosting</span>:
                      deploy open-source or fine-tuned models behind your
                      firewall (including RAG stacks with vector DB + document
                      pipelines).
                    </p>
                  </li>
                  <li className="text-sm md:text-base flex items-start gap-2 text-gray-700">
                    <div>
                      <CheckMark />
                    </div>
                    <p className="w-fit">
                      <span className="font-bold">Ship and Maintain</span>:
                      reproducible builds, CI/CD, model registry, rollout
                      strategies, monitoring, and cost/perf controls.
                    </p>
                  </li>
                  <li className="text-sm md:text-base flex items-start gap-2 text-gray-700">
                    <div>
                      <CheckMark />
                    </div>
                    <p className="w-fit">
                      <span className="font-bold">Compliance-ready setup</span>:
                      policies, evidence collection, logging retention, access
                      reviews, and deployment documentation.
                    </p>
                  </li>
                </ul>
              </div>

              <img
                src={What_you_get_Img}
                alt="Man working with data analytics"
                className="h-auto w-full max-w-[640px] object-cover"
              />
            </div>

            <div className="flex flex-col md:flex-row-reverse items-center mt-20 gap-12 relative z-10">
              {/* Left side - text content */}
              <div className="space-y-3 md:space-y-6">
                <div className="space-y-1 md:space-y-3">
                  <h3 className=" text-2xl xl:text-[32px] font-bold">
                    What you get
                  </h3>
                </div>

                <ul className="space-y-2">
                  <li className="text-sm md:text-base flex items-start gap-2 text-gray-700">
                    <div>
                      <CheckMark />
                    </div>
                    <p className="w-fit">
                      <span className="font-bold">
                        Workload isolation by design
                      </span>
                      : tenant separation (VM/Kubernetes), GPU partitioning
                      where appropriate, network segmentation, and strict IAM.
                    </p>
                  </li>
                  <li className="text-sm md:text-base flex items-start gap-2 text-gray-700">
                    <div>
                      <CheckMark />
                    </div>
                    <p className="w-fit">
                      <span className="font-bold">
                        Data security end-to-end
                      </span>
                      : encryption in transit/at rest, key management, secrets
                      handling, audit logs, and least-privilege access.
                    </p>
                  </li>
                  <li className="text-sm md:text-base flex items-start gap-2 text-gray-700">
                    <div>
                      <CheckMark />
                    </div>
                    <p className="w-fit">
                      <span className="font-bold">Private model hosting</span>:
                      deploy open-source or fine-tuned models behind your
                      firewall (including RAG stacks with vector DB + document
                      pipelines).
                    </p>
                  </li>
                  <li className="text-sm md:text-base flex items-start gap-2 text-gray-700">
                    <div>
                      <CheckMark />
                    </div>
                    <p className="w-fit">
                      <span className="font-bold">Ship and Maintain</span>:
                      reproducible builds, CI/CD, model registry, rollout
                      strategies, monitoring, and cost/perf controls.
                    </p>
                  </li>
                  <li className="text-sm md:text-base flex items-start gap-2 text-gray-700">
                    <div>
                      <CheckMark />
                    </div>
                    <p className="w-fit">
                      <span className="font-bold">Compliance-ready setup</span>:
                      policies, evidence collection, logging retention, access
                      reviews, and deployment documentation.
                    </p>
                  </li>
                </ul>
              </div>

              <img
                src={Inference_Choose_Us_Img}
                alt="Man working with data analytics"
                className="h-auto w-full max-w-[640px] object-cover"
              />
            </div>
          </div>
        </section>

        <NewFooter>
          <BookConsultation
            title="Start Your AI Integration Journey With Us"
            text="Let's discuss your integration challenges and design solutions that make AI a seamless part of your operations. "
            btnText="Contact us"
            href="/contact"
          />
        </NewFooter>
      </main>
    </>
  );
}
